<template>
  <section
    id="campus"
    class="campus"
  >
    <img
      class="campus__stars"
      src="/assets/pictos/decor-campus.svg"
      alt=""
      aria-hidden="true"
    >
    <div class="container">
      <div class="campus__intro">
        <div class="campus__text">
          <p class="section-kicker">{{ data.campus.kicker }}</p>
          <h2 class="section-title">{{ data.campus.title }}</h2>
          <p
            class="campus__body"
            v-html="bodyHtml"
          />
          <a
            class="btn-pill is-yellow-outline campus__brochure"
            :class="{ 'is-disabled': !hasBrochure }"
            :href="hasBrochure ? data.site.brochureUrl : null"
            :target="hasBrochure ? '_blank' : null"
            rel="noopener"
            download
            @click="onBrochure"
          >
            <BtnStar />
            {{ data.campus.brochureLabel }}
          </a>
        </div>
        <div class="campus__image">
          <img
            :src="data.campus.image"
            alt=""
          >
        </div>
      </div>

      <h2 class="section-title campus__advantages-title">{{ data.campus.advantagesTitle }}</h2>
      <ul class="campus__advantages">
        <li
          v-for="adv in data.campus.advantages"
          :key="adv.label"
        >
          <span class="campus__advantage-icon">
            <img
              :src="adv.icon"
              alt=""
            >
          </span>
          <span class="campus__advantage-label">{{ adv.label }}</span>
        </li>
      </ul>

      <h2 class="section-title campus__offers-title">{{ data.offers.title }}</h2>
      <div class="campus__offers">
        <article
          v-for="offer in data.offers.items"
          :key="offer.title"
          class="offer-card"
          :class="`is-${offer.color}`"
          tabindex="0"
        >
          <div class="offer-card__front">
            <img
              :src="offer.image"
              alt=""
            >
            <h3>{{ offer.title }}</h3>
          </div>
          <div class="offer-card__back">
            <p v-html="toHtml(offer.text)" />
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<script>
import BtnStar from '@/components/BtnStar.vue'
import { toHtml } from '@/content/richtext'

export default {
  name: 'CampusSection',
  components: { BtnStar },
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  computed: {
    bodyHtml() {
      return toHtml(this.data.campus.body)
    },
    // vrai seulement si une vraie brochure est hébergée (pas le placeholder '#')
    hasBrochure() {
      const u = this.data.site.brochureUrl
      return !!u && u !== '#'
    }
  },
  methods: {
    toHtml,
    onBrochure(e) {
      if (!this.hasBrochure) e.preventDefault()
    }
  }
}
</script>

<style lang="scss" scoped>
.campus {
  position: relative;
  background: $navy;
  color: $white;
  padding: 110px 0 96px;
  overflow: hidden;
}

// décor : collé au coin haut-gauche du viewport, visible seulement sur grand
// écran (sous 1400px il chevaucherait le texte du container)
.campus__stars {
  display: none;
  position: absolute;
  // sous le bandeau jaune incliné (qui déborde d'environ 37px dans la section),
  // sinon la moitié haute du décor est masquée
  top: 46px;
  left: 0;
  width: clamp(140px, 11vw, 180px);
  pointer-events: none;
  user-select: none;

  // à partir de 1600px la marge du container (>=200px) dépasse la largeur du
  // décor : il reste entièrement visible sans toucher le kicker/titre
  @media (min-width: 1600px) {
    display: block;
  }
}

.campus__intro .section-title {
  white-space: pre-line;
}

.campus__intro {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: clamp(30px, 5vw, 80px);
  align-items: stretch;
}

.campus__body {
  margin-top: 26px;
  font-size: 15px;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.92);

  :deep(strong) {
    font-weight: 800;
    color: $white;
  }
}

.campus__brochure {
  margin-top: 32px;

  // pas encore de PDF hébergé (brochureUrl '#') : bouton inerte
  &.is-disabled {
    opacity: 0.55;
    cursor: default;

    &:hover {
      transform: none;
      background: none;
      color: $yellow;
    }
  }
}

// l'image reste dans le container, alignée verticalement avec le bloc texte
.campus__image {
  position: relative;

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 6px;
  }
}

.campus__advantages-title {
  margin-top: 90px;
}

.campus__advantages {
  list-style: none;
  margin: 44px 0 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 24px;

  li {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    text-align: center;
  }
}

.campus__advantage-icon {
  width: 78px;
  height: 78px;
  border-radius: 20px;
  background: $red;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 58px;
    height: 58px;
  }
}

.campus__advantage-label {
  font-size: 15px;
  font-weight: 700;
  line-height: 1.3;
  white-space: pre-line;
}

.campus__offers-title {
  margin-top: 100px;
}

.campus__offers {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: clamp(20px, 3vw, 44px);
  margin-top: 44px;
}

// Au survol, le visuel laisse place au descriptif (PDF animations p.3)
.offer-card {
  position: relative;
  aspect-ratio: 1 / 0.94;
  border-radius: 22px;
  overflow: hidden;
  outline: none;

  &__front {
    position: absolute;
    inset: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    h3 {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      padding: 0 18px;
      font-size: clamp(28px, 3.2vw, 42px);
      font-weight: 700;
      line-height: 1.1;
      color: $white;
      text-shadow: 0 2px 14px rgba(0, 0, 0, 0.25);
    }
  }

  &__back {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    padding: clamp(18px, 2.4vw, 34px);
    opacity: 0;
    transition: opacity 0.3s ease;

    p {
      font-size: clamp(13px, 1.3vw, 16px);
      line-height: 1.6;
      font-weight: 500;
      text-align: center;
    }
  }

  &.is-yellow .offer-card__back {
    background: $yellow;
    color: $navy;
  }

  &.is-red .offer-card__back {
    background: $red;
    color: $white;
  }

  &:hover .offer-card__back,
  &:focus-visible .offer-card__back {
    opacity: 1;
  }
}

@include tablet-down {
  .campus__advantages {
    grid-template-columns: repeat(3, 1fr);
    gap: 32px 16px;
  }
}

@include mobile {
  .campus {
    padding: 72px 0 64px;
  }

  .campus__intro {
    grid-template-columns: 1fr;
  }

  // l'image d'abord (maquette mobile)
  .campus__image {
    order: -1;
    margin: 0 -22px 26px;

    img {
      position: static;
      height: auto;
      max-height: 340px;
    }
  }

  .campus__advantages-title,
  .campus__offers-title {
    margin-top: 64px;
  }

  // carrousel horizontal (maquette mobile)
  .campus__offers {
    display: flex;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    gap: 16px;
    margin-inline: -22px;
    padding: 4px 22px 12px;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .offer-card {
    flex: 0 0 78%;
    scroll-snap-align: center;
  }
}
</style>
